import {
  deriveKey,
  loginMessage,
  newRecoveryPhrase,
  registrationMessage,
  seal,
  seedFromPhrase,
  sign,
  unseal,
  wipe,
  type Chain,
  type SealedSeed,
} from '@xchange/wallet';
import type { MeResponse } from '@xchange/shared';
import { api, patiently } from './api.js';
import type { Platform } from './platform/index.js';

/**
 * The person's wallet, on their device.
 *
 * The recovery phrase is made here and never leaves: the phrase itself is
 * sealed under the PIN (a phrase cannot be recovered from its seed, and the
 * person must be able to see it again) and the sealed blob goes to Telegram's cloud storage (so it follows the
 * person to a new phone) and to localStorage (so it opens without a round
 * trip). The server learns only the addresses, each signed by its own key.
 * The seed is in memory only while a signature is being made.
 */
const KEY = 'wallet.v1';

export async function loadSealed(platform: Platform): Promise<SealedSeed | null> {
  let text: string | null = null;
  try {
    text = localStorage.getItem(KEY);
  } catch {
    /* no local storage */
  }
  // A browser has no cloud copy: the device is the only store, and the list
  // of wallets below is kept there too. Only Telegram's cloud is asked.
  if (text === null && platform.kind !== 'web') {
    text = await platform.storage.get(KEY);
    if (text !== null) {
      try {
        localStorage.setItem(KEY, text);
      } catch {
        /* fine */
      }
    }
  }
  if (text === null) return null;
  const parsed = JSON.parse(text) as Partial<SealedSeed>;
  return parsed.v === 1 && parsed.data !== undefined ? (parsed as SealedSeed) : null;
}

/**
 * Keep the sealed wallet on the phone and in Telegram's cloud. Either one is
 * enough to open it here; the cloud copy is what follows the person to a new
 * phone. Only when neither can keep it is that a failure: an older Telegram
 * refusing its cloud must not stop someone making a wallet.
 *
 * Returns whether the cloud copy is really there, read back after writing.
 * On 8 October 2026 a cloud write that never answered was taken as success;
 * the phone was the only copy, Telegram was reinstalled, and 92,000 Gaju
 * became unreachable. The person is told, and the phrase is shown and typed
 * back before the wallet is called ready, whatever this returns.
 */
async function store(platform: Platform, sealed: SealedSeed): Promise<{ cloudBacked: boolean }> {
  const text = JSON.stringify(sealed);
  let local = false;
  try {
    localStorage.setItem(KEY, text);
    local = localStorage.getItem(KEY) === text;
  } catch {
    local = false;
  }
  let cloudBacked = false;
  try {
    await platform.storage.set(KEY, text);
    cloudBacked = (await platform.storage.get(KEY)) === text;
  } catch (e) {
    if (!local)
      throw new Error(
        `Neither this phone nor Telegram could keep the wallet (${e instanceof Error ? e.message : String(e)})`,
      );
  }
  return { cloudBacked };
}

/**
 * A new wallet is not ready until its phrase has been typed back. The mark is
 * on the phone beside the wallet, so closing the app mid-way brings the
 * person back to the words, not to trading.
 */
const UNCONFIRMED_KEY = 'wallet.v1.unconfirmed';
export function needsConfirmation(): boolean {
  try {
    return localStorage.getItem(UNCONFIRMED_KEY) !== null;
  } catch {
    return false;
  }
}
export function markConfirmed(): void {
  try {
    localStorage.removeItem(UNCONFIRMED_KEY);
  } catch {
    /* fine */
  }
}
function markUnconfirmed(): void {
  try {
    localStorage.setItem(UNCONFIRMED_KEY, '1');
  } catch {
    /* fine */
  }
}

/** The same twelve words, however they were spaced or cased. */
export function samePhrase(a: string, b: string): boolean {
  const norm = (s: string): string => s.trim().toLowerCase().split(/\s+/).join(' ');
  return norm(a) === norm(b);
}

/** Sign both chains' registrations from one seed, then forget the seed. */
async function register(seed: Uint8Array, telegramId: string): Promise<void> {
  for (const chain of ['solana', 'gajumaru'] as const) {
    const key = deriveKey(seed, chain);
    const signature = sign(key, registrationMessage(telegramId, chain, key.address));
    await api.registerWallet({
      chain,
      address: key.address,
      signature: btoa(String.fromCharCode(...signature)),
    });
    wipe(key.secret);
  }
}

/**
 * A brand-new wallet, or one restored from a phrase the person typed. A new
 * one comes back with its phrase, to be shown and typed back before the
 * wallet is called ready; a restored one needs no such step, the person
 * has the words.
 */
export async function createWallet(
  platform: Platform,
  /** The account to register the addresses under; null when there is no account yet (a browser, before sign-in). */
  telegramId: string | null,
  pin: string,
  phrase?: string,
): Promise<{ phrase: string | null; cloudBacked: boolean }> {
  const fresh = phrase === undefined;
  const words = phrase ?? newRecoveryPhrase();
  const seed = seedFromPhrase(words);
  const phraseBytes = new TextEncoder().encode(words);
  try {
    const sealed = await seal(phraseBytes, pin);
    const { cloudBacked } = await store(platform, sealed);
    if (fresh) markUnconfirmed();
    if (platform.kind === 'web') rememberActiveWallet(deriveKey(seed, 'solana').address);
    if (telegramId !== null) await register(seed, telegramId);
    return { phrase: fresh ? words : null, cloudBacked };
  } finally {
    wipe(seed, phraseBytes);
  }
}

/**
 * Sign in from a browser with the wallet on this device: the Solana key
 * signs a one-time challenge, the server answers with the account that owns
 * the address (or a new one), and any address the account has not registered
 * yet is registered now, under that account. The seed lives for the length
 * of the call and is wiped after.
 */
export async function signInWithWallet(
  platform: Platform,
  pin: string,
  ref?: string,
): Promise<MeResponse> {
  return withWallet(platform, pin, async ({ seed }) => {
    const sol = deriveKey(seed, 'solana');
    try {
      const c = await patiently(() => api.loginChallenge({ address: sol.address }));
      const signature = sign(sol, loginMessage(sol.address, c.nonce, c.expiresAt));
      const me = await patiently(() =>
        api.loginWithWallet({
          address: sol.address,
          nonce: c.nonce,
          signature: btoa(String.fromCharCode(...signature)),
          ...(ref === undefined ? {} : { ref }),
        }),
      );
      const w = await patiently(() => api.wallet());
      for (const chain of ['solana', 'gajumaru'] as const) {
        const key = deriveKey(seed, chain);
        if (w.addresses[chain] !== key.address) {
          const s = sign(key, registrationMessage(me.telegramId, chain, key.address));
          await patiently(() =>
            api.registerWallet({
              chain,
              address: key.address,
              signature: btoa(String.fromCharCode(...s)),
            }),
          );
        }
        wipe(key.secret);
      }
      return me;
    } finally {
      wipe(sol.secret);
    }
  });
}

/** Open the wallet for one action. The callback gets the phrase and seed; both are wiped after. */
export async function withWallet<T>(
  platform: Platform,
  pin: string,
  action: (opened: { phrase: string; seed: Uint8Array }) => Promise<T> | T,
  /** Another wallet on the list instead of the open one. */
  walletId?: string,
): Promise<T> {
  const sealed =
    walletId === undefined || walletId === activeWalletId()
      ? await loadSealed(platform)
      : sealedOf(walletId);
  if (sealed === null) throw new Error('No wallet on this device');
  const phraseBytes = await unseal(sealed, pin);
  const seed = seedFromPhrase(new TextDecoder().decode(phraseBytes));
  try {
    return await action({ phrase: new TextDecoder().decode(phraseBytes), seed });
  } finally {
    wipe(seed, phraseBytes);
  }
}

export function addressesOf(seed: Uint8Array): Record<Chain, string> {
  const s = deriveKey(seed, 'solana');
  const g = deriveKey(seed, 'gajumaru');
  wipe(s.secret, g.secret);
  return { solana: s.address, gajumaru: g.address };
}

/**
 * Does this phrase open the wallet the account already uses? Asked before a
 * restore on a phone that lost its copy (Telegram reinstalled, the cloud
 * backup never arrived). The server lets an address change, so a different
 * phrase would quietly point the account at an empty wallet while the money
 * stayed at the old one.
 */
export function phraseOpens(phrase: string, solanaAddress: string): boolean {
  const seed = seedFromPhrase(phrase);
  try {
    return addressesOf(seed).solana === solanaAddress;
  } finally {
    wipe(seed);
  }
}

export { newRecoveryPhrase };

// ---- several wallets on one device (browser) -------------------------------

/**
 * A browser can hold more than one wallet: each sealed under its own PIN,
 * listed here by its Solana address and the name its account chose. The one
 * under `wallet.v1` is the active one, so everything above keeps working;
 * switching copies another entry into that slot. Telegram keeps one wallet,
 * in its own cloud, and never sees this list.
 */
export interface WalletEntry {
  readonly id: string;
  readonly solana: string;
  readonly label: string;
  readonly sealed: string;
  readonly createdAt: string;
}
const LIST_KEY = 'wallets.v1';
const ACTIVE_KEY = 'wallets.v1.active';

export function listWallets(): WalletEntry[] {
  try {
    const raw = localStorage.getItem(LIST_KEY);
    const list = raw === null ? [] : (JSON.parse(raw) as WalletEntry[]);
    // The active slot from before this list existed is a wallet too.
    const active = localStorage.getItem(KEY);
    if (active !== null && !list.some((w) => w.sealed === active)) {
      const entry: WalletEntry = {
        id: `w${Date.now().toString(36)}`,
        solana: '',
        label: 'My wallet',
        sealed: active,
        createdAt: new Date().toISOString(),
      };
      list.unshift(entry);
      localStorage.setItem(LIST_KEY, JSON.stringify(list));
      localStorage.setItem(ACTIVE_KEY, entry.id);
    }
    return list;
  } catch {
    return [];
  }
}

export function activeWalletId(): string | null {
  try {
    const id = localStorage.getItem(ACTIVE_KEY);
    return id !== null && listWallets().some((w) => w.id === id)
      ? id
      : (listWallets()[0]?.id ?? null);
  } catch {
    return null;
  }
}

/** Put the wallet just sealed into `wallet.v1` on the list, as the active one. */
export function rememberActiveWallet(solana: string): void {
  try {
    const active = localStorage.getItem(KEY);
    if (active === null) return;
    const list = listWallets().filter((w) => w.sealed !== active);
    const entry: WalletEntry = {
      id: `w${Date.now().toString(36)}`,
      solana,
      label: `Wallet ${solana.slice(0, 4)}…${solana.slice(-4)}`,
      sealed: active,
      createdAt: new Date().toISOString(),
    };
    localStorage.setItem(LIST_KEY, JSON.stringify([entry, ...list]));
    localStorage.setItem(ACTIVE_KEY, entry.id);
  } catch {
    /* fine: one wallet still works */
  }
}

/** The account's chosen name beside the active wallet, so the list reads well. */
export function labelActiveWallet(label: string): void {
  try {
    const id = activeWalletId();
    if (id === null) return;
    const list = listWallets().map((w) => (w.id === id ? { ...w, label } : w));
    localStorage.setItem(LIST_KEY, JSON.stringify(list));
  } catch {
    /* fine */
  }
}

/** Make another wallet on the list the active one. Its own PIN opens it. */
export function switchWallet(id: string): boolean {
  try {
    const w = listWallets().find((x) => x.id === id);
    if (w === undefined) return false;
    localStorage.setItem(KEY, w.sealed);
    localStorage.setItem(ACTIVE_KEY, w.id);
    localStorage.removeItem(UNCONFIRMED_KEY);
    return true;
  } catch {
    return false;
  }
}

/** Clear the active slot so a new wallet can be made beside the others. */
export function startAnotherWallet(): void {
  try {
    listWallets();
    localStorage.removeItem(KEY);
    localStorage.removeItem(UNCONFIRMED_KEY);
  } catch {
    /* fine */
  }
}

/**
 * Take a wallet off this device. Only its twelve words open it again, here or
 * anywhere; nothing leaves the device and nothing on chain changes. When it
 * is the open one, the next wallet on the list becomes the open one, or none.
 */
export function forgetWallet(id: string): { wasOpen: boolean; next: string | null } {
  try {
    const wasOpen = activeWalletId() === id;
    const gone = listWallets().find((w) => w.id === id);
    const list = listWallets().filter((w) => w.id !== id);
    if (wasOpen || localStorage.getItem(KEY) === gone?.sealed) {
      // Cleared before the list is read again, or the slot would come back as "My wallet".
      localStorage.removeItem(KEY);
      localStorage.removeItem(ACTIVE_KEY);
      localStorage.removeItem(UNCONFIRMED_KEY);
    }
    localStorage.setItem(LIST_KEY, JSON.stringify(list));
    forgetSeen(id);
    const next = wasOpen ? (list[0]?.id ?? null) : activeWalletId();
    if (wasOpen && next !== null) switchWallet(next);
    return { wasOpen, next };
  } catch {
    return { wasOpen: false, next: null };
  }
}

/**
 * The copy kept beside the person (Telegram's cloud; in a browser, a second
 * local key) goes too, but only when it is this very wallet: in Telegram it
 * would otherwise bring the wallet straight back on the next open.
 */
export async function forgetStoredCopy(platform: Platform, sealed: string): Promise<void> {
  try {
    if ((await platform.storage.get(KEY)) === sealed) await platform.storage.remove(KEY);
  } catch {
    /* the device copy is gone; that is what was asked */
  }
}

/** The sealed text of a wallet on the list, to open it without making it the active one. */
export function sealedOf(id: string): SealedSeed | null {
  const w = listWallets().find((x) => x.id === id);
  if (w === undefined) return null;
  try {
    const parsed = JSON.parse(w.sealed) as Partial<SealedSeed>;
    return parsed.v === 1 && parsed.data !== undefined ? (parsed as SealedSeed) : null;
  } catch {
    return null;
  }
}

// ---- what each wallet held when it was last open -------------------------------

/**
 * The server answers only for the open wallet, so what another wallet holds
 * is what this device saw the last time it was open. Amounts and a count,
 * no addresses; on this device only.
 */
export interface WalletSeen {
  readonly gaju: string | null;
  readonly usdc: string | null;
  readonly xge: string | null;
  /** The Wallet chip's total in USDC units; null when there was no figure. */
  readonly worth: string | null;
  readonly orders: number;
  readonly at: string;
}
const SEEN_KEY = 'wallets.v1.seen';

function seenAll(): Record<string, WalletSeen> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return raw === null ? {} : (JSON.parse(raw) as Record<string, WalletSeen>);
  } catch {
    return {};
  }
}

export function noteWalletSeen(id: string, seen: WalletSeen): void {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify({ ...seenAll(), [id]: seen }));
  } catch {
    /* fine */
  }
}

export function walletSeen(id: string): WalletSeen | null {
  return seenAll()[id] ?? null;
}

function forgetSeen(id: string): void {
  const all = seenAll();
  if (!(id in all)) return;
  const rest = Object.fromEntries(Object.entries(all).filter(([k]) => k !== id));
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(rest));
  } catch {
    /* fine */
  }
}
